import { Languages } from 'lucide-react';
import { useTranslation, type SupportedLocale } from '@dukanti/shared';
import { cn } from '@/lib/utils';

// F-027 §3.2 / §5.2 / D-14 — cycle button that walks ar → he → en → ar.
// Reads `i18n.language` directly via useTranslation() so there is NO local
// state mirror (dukanti-conventions skill §4 — i18n-language-single-source-
// of-truth). A language-switcher test pins that constraint.
const LANGUAGE_CYCLE: readonly SupportedLocale[] = ['ar', 'he', 'en'] as const;

function nextLocale(current: string): SupportedLocale {
  const idx = LANGUAGE_CYCLE.indexOf(current as SupportedLocale);
  if (idx === -1) return LANGUAGE_CYCLE[0]!;
  return LANGUAGE_CYCLE[(idx + 1) % LANGUAGE_CYCLE.length]!;
}

function labelForLocale(locale: string): string {
  if (locale === 'ar') return 'ع';
  if (locale === 'he') return 'ע';
  if (locale === 'en') return 'EN';
  return locale.toUpperCase();
}

export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const current = i18n.language;

  return (
    <button
      type="button"
      data-testid="admin-shell-language-switcher"
      aria-label={t('admin.nav.languageSwitcher')}
      onClick={() => void i18n.changeLanguage(nextLocale(current))}
      className={cn(
        'border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring inline-flex h-9 min-w-9 shrink-0 items-center justify-center gap-1.5 rounded-md border px-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
      )}
    >
      <Languages className="size-4" aria-hidden="true" />
      <span data-testid="admin-shell-language-switcher-label">{labelForLocale(current)}</span>
    </button>
  );
}
